import React, {useContext, useRef} from "react";
import {
  ActivationAlgo, AnalysisHelper, AutoTradeSetupStatusEnumHelper, DateHelper,
  ISignalAlertActivation as Entity, ISignalAlertDetails, MathEx,
  SignalAlertPerfTypeEnum,
  SignalAlertPerfTypeEnumHelper
} from "predictagram-lib";
import {adminApiServiceCommon} from "../../../services/AdminApiService";
import {useApiServiceState} from "../../../_hooks/useApiServiceState";
import {Spinner} from "../../common/Spinner";
import {getCoreRowModel, getSortedRowModel, SortingState, useReactTable} from "@tanstack/react-table";
import {ReactTableHelper} from "../../../_utils/ReactTableHelper";
import {DataGrid} from "../common/DataGrid";
import {useNavigate} from "react-router-dom";
import {UrlHelper} from "../../../_utils/UrlHelper";
import {MessengerContext, Severity} from "../../common/messenger";
import {ArrowClockwise, Download} from "react-bootstrap-icons";
import StatusEnumHelper = ActivationAlgo.StatusEnumHelper;
import StatusEnum = ActivationAlgo.StatusEnum;
import {CsvHelper} from "../../../_utils/CsvHelper";
import {PageNotes} from "../common/PageNotes";

export const AdminActivationCombos: React.FunctionComponent = () => {
  const msgrContext = useContext(MessengerContext);
  const navigate = useNavigate();
  const refStatusId = useRef(StatusEnum.ACTIVE);
  const refAlgoType = useRef('' as any as SignalAlertPerfTypeEnum);
  const supplier = async() => {
    const data = await adminApiServiceCommon.getActivationCombos({
      signalAlertActivationStatusIds: refStatusId.current ? [refStatusId.current]:undefined as any,
      signalAlertActivationAlgoTypeIds: refAlgoType.current ? [refAlgoType.current]: undefined as any,
    });
    return data;
  }

  const statuses = Array.from(StatusEnumHelper.getNames().entries()).map(([s,name])=>{
    return {label:name,value:s}
  });

  const algos = Array.from(SignalAlertPerfTypeEnumHelper.names().entries()).map(([s,name])=>{
    return {label:name,value:s}
  });

  const deleteItem = async(id:number)=>{
    if (window.confirm(`Delete item #${id}?`)) {
      const res = await adminApiServiceCommon.deleteActivationComboById(id);
      msgrContext.setMessage({ body: res?'Removed':'Not Removed' }, true);
      if (res) {
        reload();
      }
    }
  }
  const setStatus = async(id:number,statusId: ActivationAlgo.StatusEnum)=>{
    // @TODO: refactor the  wrapper
    try {
      await adminApiServiceCommon.updateActivationComboById(id,{statusId:statusId});
      return true;
    } catch (e: any) {
      msgrContext.setMessage({body: e.message}, true, Severity.FATAL);
    }

  }
  const downloadReport = async(activationId?:number) => {
    let fileName = `activation-combo-perf-${activationId||'all'}` ;
    const perfHist = await adminApiServiceCommon.getActivationCombosPerformance(
      {signalAlertActivationIds: activationId?[activationId]:undefined});
    CsvHelper.downloadCsv(fileName, perfHist.map(a => {
      return {
        algoType: SignalAlertPerfTypeEnumHelper.names().get(a.algoTypeId),
        activationId: a.activationId,
        activationStatus: StatusEnumHelper.getNames().get(a.activationStatusId),
        activationName: a.activationName,
        alertId: a.alertId,
        alertName: a.alertName,
        operativeOptionSetups: a.operativeOptionSetups,
        operativeEquitySetups: a.operativeEquitySetups,
        date: a.date,
        score: a.score,
        trades: a.trades,
      }
    }));
  }

  const [sorting, setSorting] = React.useState<SortingState>([])
  const {items, state, setSupplier, reload} = useApiServiceState<Entity[]>(()=>supplier());
  const columnHelper = ReactTableHelper.createHelper<Entity>();
  const downloadButton = (activationId?:number)=><button className="btn btn-primary mt-0 pt-1" onClick={()=>downloadReport(activationId)}><Download/></button>
  const columns = [
    columnHelper.columnBuilder('id', {header: 'ID'}).number().build(),
    columnHelper.columnBuilder('statusId', {
      header: 'Status',
      cell: (info)=>{
        const id = parseInt(info.cell.row.getValue('id'));
        return (
          <select defaultValue={info.getValue()} onChange={(e)=>setStatus(id, parseInt(e.target.value) as any)}>
            {statuses.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
            // <DropDownGeneric label={''} value={info.getValue()} name={'statusId'} options={statuses} onChange={(v)=>{setStatus(id, v);return true}}/>
        )
      }
    }).build(),
    columnHelper.columnBuilder('algoTypeId', {header:'Algo',cell:(info)=>{
        return SignalAlertPerfTypeEnumHelper.names().get(parseInt(info.cell.row.getValue('algoTypeId')));
    }}).build(),
    columnHelper.columnBuilder('name', {header: 'Name', cell:(info)=>{
      return <>
        {info.cell.row.original.name}
        {downloadButton(info.cell.row.original.id)}
      </>
    }}).build(),
    columnHelper.columnBuilder('notes', {header: 'Notes'}).build(),
    columnHelper.columnBuilder('phoneNumber', {header: 'Phone'}).build(),
    columnHelper.columnBuilder('performance', {header: 'Performance', cell:(info)=>{
      return (info.cell.row.original.performance||[]).map((perf,idx)=>
        <div key={idx}>{perf.date}: {MathEx.round((perf.score||0) * 100, 2)}% trades {perf.trades}</div>
      )
    }}).build(),
    columnHelper.columnBuilder('createdAt', {header:'Created',cell:(info)=>{
      return DateHelper.dateTimeFormatUs(info.cell.row.original.createdAt)
    }}).build(),
    columnHelper.columnBuilder('buttons', {
      header: '',
      cell: (info)=>{
        const id = parseInt(info.cell.row.getValue('id'));
        const url = UrlHelper.getAdminActivationCombo(id);
        return (<>
          <div className="d-flex gap-1">
            <div className="d-flex flex-column gap-3"><button className="btn btn-primary" onClick={()=>navigate(url)}>Edit</button></div>
            <div className="d-flex flex-column gap-3"><button className="btn btn-primary" onClick={()=>deleteItem(id)}>Delete</button></div>
          </div>
          </>
        )
      }
    }).build(),
  ];


  const table = useReactTable<Entity>({
    data: items || [],
    columns: columns as any,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {sorting},
  });

  return (
    <>
      <div key='title' className="d-flex justify-content-between align-items-end">
        <div className="page-title">Activation Combos</div>
        <button className="btn btn-primary mt-2" onClick={() => navigate(UrlHelper.getAdminActivationCombo())}>Create New</button>
      </div>
      <div key='filter' className="d-flex align-items-center gap-4 justify-content-center">
        <div key='status'>
          Status:
          <select defaultValue={refStatusId.current} onChange={(e)=>refStatusId.current=parseInt(e.target.value) as any}>
            <option value=''>All</option>
            {statuses.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div key='algo'>
          Algo:
          <select defaultValue={refAlgoType.current as any} onChange={(e)=>refAlgoType.current=parseInt(e.target.value) as any}>
            <option value=''>All</option>
            {algos.map(s=><option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div key='search'><button className="btn btn-primary" onClick={() => reload()}><ArrowClockwise /></button></div>
        <div key='download'>{downloadButton()}</div>
      </div>
      <div key='space'>&nbsp;</div>
      {state.isLoading() && <Spinner />}
      {state.isError() && <div key='err'>{state.error()?.message}</div>}
      {state.isLoaded() &&
       <DataGrid table={table}/>
      }
      <PageNotes>
        <ul>
          <li>
            Activation combo statuses:
            <ul>
              <li><b>Active</b> - combo is monitored by system, "Performance" is tracked, auto-trade setups are activated.</li>
              <li><b>Paused Activate</b> - combo is monitored by system, "Performance" is tracked, but setups aren't activated.</li>
              <li><b>Paused</b> - combo is fully on hold.</li>
            </ul>
          </li>
        </ul>
      </PageNotes>
    </>
  );
};


